import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";

// Task #4384 — A newly-approved host's starter content (Buy Box criteria,
// underwriting defaults, …) is seeded fire-and-forget on approval, so their
// very first visit to the Buy Box / Underwriting pages can land before the
// seeds finish. Rather than flash an empty-looking form (which reads like a
// bug), those pages show this "your account is being set up…" pending state
// while seeding is in flight and poll until the rows appear.

type MeResponse = {
  starterSeedPending?: boolean;
};

/**
 * True while the server reports a starter-content seed run is still in flight
 * for the current host (recency-bounded server-side so a stuck marker
 * self-heals). Reuses the cached `/api/app/auth/me` query so it adds no extra
 * request when AppLayout has already loaded it.
 */
export function useStarterSeedPending(): boolean {
  const meQuery = useQuery<MeResponse>({
    queryKey: ["/api/app/auth/me"],
  });
  return meQuery.data?.starterSeedPending === true;
}

/**
 * Full-card pending placeholder shown in place of an empty form while a
 * newly-approved host's defaults are still being prepared.
 */
export function AccountSetupPending({ label }: { label: string }) {
  return (
    <div
      className="bg-white border border-gray-200 rounded-xl p-10 flex flex-col items-center justify-center text-center"
      data-testid="state-account-setup-pending"
    >
      <Loader2 className="h-8 w-8 text-blue-600 animate-spin mb-4" />
      <h2 className="text-lg font-semibold text-gray-900 mb-1">Your account is being set up…</h2>
      <p className="text-sm text-gray-500 max-w-md">
        We're pre-filling your {label} with sensible starter defaults. This usually takes just a
        few seconds — they'll appear here automatically as soon as they're ready.
      </p>
    </div>
  );
}
